import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { TikTokAnalytics } from "@/components/TikTokAnalytics";
import { ReportAnalytics } from "@/components/ReportAnalytics";
import { PriceAutomationPanel } from "@/components/PriceAutomationPanel";
import { AffiliatePanel } from "@/components/AffiliatePanel";
import {
  AnchorChecker,
  DisavowGenerator,
  RedirectMap,
  OutreachPack,
  SeoDashboardLinks,
  TitleTests,
  WeeklyRankings,
} from "@/components/AdminSeoPanels";

import { adminGrantHistory, adminGrantPacks, adminGrantSpins, adminListMembers, adminSetWriter, amIAdmin } from "@/lib/admin.functions";
import { useSession } from "@/lib/user-data";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin Console — Pal Stars Booster Packs" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Pal Stars staff console: grant booster packs to members and review the pack grant history." },
      { property: "og:title", content: "Admin Console — Pal Stars" },
      { property: "og:description", content: "Staff-only console for granting Pal Stars booster packs." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const { session, loading } = useSession();
  const checkAdmin = useServerFn(amIAdmin);
  const listMembers = useServerFn(adminListMembers);
  const grant = useServerFn(adminGrantPacks);
  const grantSpins = useServerFn(adminGrantSpins);
  const history = useServerFn(adminGrantHistory);
  const setWriter = useServerFn(adminSetWriter);

  const [admin, setAdmin] = useState<boolean | null>(null);
  const [members, setMembers] = useState<Awaited<ReturnType<typeof adminListMembers>>>([]);
  const [log, setLog] = useState<Awaited<ReturnType<typeof adminGrantHistory>>>([]);
  const [packs, setPacks] = useState(5);
  const [spins, setSpins] = useState(5);
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [notice, setNotice] = useState("");


  const refresh = useCallback(() => {
    listMembers({}).then(setMembers).catch(() => setNotice("Could not load members."));
    history({}).then(setLog).catch(() => {});
  }, [listMembers, history]);

  useEffect(() => {
    if (!session) return;
    checkAdmin({})
      .then((r) => {
        setAdmin(r.admin);
        if (r.admin) refresh();
      })
      .catch(() => setAdmin(false));
  }, [session, checkAdmin, refresh]);

  const give = async (userId: string, name: string) => {
    setNotice("");
    try {
      const res = await grant({ data: { userId, packs, note } });
      setNotice(`Gave ${packs} pack${packs === 1 ? "" : "s"} to ${name} — their balance is now ${res.bonusPacks}.`);
      refresh();
    } catch {
      setNotice("That grant did not go through. Check the amount and try again.");
    }
  };

  const giveSpins = async (userId: string, name: string) => {
    setNotice("");
    try {
      const res = await grantSpins({ data: { userId, spins, note } });
      setNotice(`Gave ${spins} Palsino spin${spins === 1 ? "" : "s"} to ${name} — they now have ${res.bonusSpins} bonus spins.`);
      refresh();
    } catch {
      setNotice("That spin grant did not go through. Check the amount and try again.");
    }
  };


  const shown = members.filter((m) => m.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <h1 className="mt-2 font-display text-3xl font-bold">Admin console</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Grant booster packs or Palsino spins, and run the SEO growth tools. These are separate balances: packs can be opened, while only spins power the Palsino reels. Grants land instantly and never expire.
      </p>


      {loading || admin === null ? (
        <p className="mt-8 text-sm text-muted-foreground">Checking your access…</p>
      ) : !session ? (
        <p className="mt-8 text-sm">
          <Link to="/auth" className="text-primary hover:underline">Sign in</Link> with a staff account to continue.
        </p>
      ) : !admin ? (
        <p className="mt-8 text-sm text-amber-400">This console is limited to Pal Stars admins.</p>
      ) : (
        <div className="mt-6 space-y-6">
          <div className="glass flex flex-wrap items-center justify-between gap-3 rounded-xl p-4">
            <p className="text-sm font-semibold">Palworld TCG blog</p>
            <div className="flex gap-3 text-sm">
              <Link to="/blog-admin" className="font-semibold text-primary hover:underline">Open blog editor →</Link>
              <Link to="/blog" className="text-muted-foreground hover:underline">View public blog</Link>
            </div>
          </div>

          <div className="glass flex flex-wrap items-end gap-3 rounded-xl p-4">
            <label className="text-xs">
              <span className="block text-muted-foreground">Palsino reel spins to give</span>
              <input
                type="number"
                min={1}
                max={100}
                value={spins}
                onChange={(e) => setSpins(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                className="mt-1 w-24 rounded-md border bg-transparent px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="block text-muted-foreground">Booster packs to give</span>

              <input
                type="number"
                min={1}
                max={500}
                value={packs}
                onChange={(e) => setPacks(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                className="mt-1 w-24 rounded-md border bg-transparent px-2 py-1.5 text-sm"
              />
            </label>
            <label className="min-w-[200px] flex-1 text-xs">
              <span className="block text-muted-foreground">Note (optional)</span>
              <input
                value={note}
                maxLength={200}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Tournament prize, support fix…"
                className="mt-1 w-full rounded-md border bg-transparent px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="block text-muted-foreground">Find member</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name"
                className="mt-1 w-48 rounded-md border bg-transparent px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          {notice && <p className="text-sm text-primary" aria-live="polite">{notice}</p>}

          <div className="glass overflow-x-auto rounded-xl p-4">
            <h2 className="text-sm font-bold">Members ({shown.length})</h2>
            <table className="mt-2 w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1">Member</th>
                  <th className="py-1">Pack balance</th>
                  <th className="py-1">Bonus spins</th>
                  <th className="py-1">Role</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="py-2 font-medium">{m.name}</td>
                    <td className="py-2 tabular-nums">{m.bonusPacks}</td>
                    <td className="py-2 tabular-nums">{m.bonusSpins}</td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {m.admin ? "Admin" : m.writer ? "Writer" : "Member"}
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => void give(m.id, m.name)}
                          className="rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:brightness-110"
                        >
                          Give {packs} booster packs
                        </button>
                        <button
                          type="button"
                          onClick={() => void giveSpins(m.id, m.name)}
                          className="rounded-md border px-3 py-1.5 text-xs font-bold hover:bg-card"
                        >
                          Give {spins} Palsino spins
                        </button>
                        {!m.admin && (
                          <button
                            type="button"
                            onClick={() => {
                              void setWriter({ data: { userId: m.id, writer: !m.writer } })
                                .then(() => listMembers({}).then(setMembers))
                                .then(() => setNotice(m.writer ? `Writer access removed for ${m.name}.` : `${m.name} can now write blog articles.`))
                                .catch(() => setNotice("Could not change writer access."));
                            }}
                            className="rounded-md border px-3 py-1.5 text-xs font-bold hover:bg-card"
                          >
                            {m.writer ? "Remove writer access" : "Make blog writer"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>
          </div>

          <div className="space-y-4">
            <h2 className="text-base font-bold">SEO growth tools</h2>
            <OutreachPack />
            <SeoDashboardLinks />
            <WeeklyRankings />
            <TitleTests />
            <AnchorChecker />
            <RedirectMap />
            <DisavowGenerator />
          </div>

          <div className="glass rounded-xl p-4">
            <h2 className="text-sm font-bold">TikTok campaign analytics</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              QR scans and link clicks from the Pal Stars TikToks, tracked through palstars.com/tiktok.
            </p>
            <div className="mt-3">
              <TikTokAnalytics />
            </div>
          </div>

          <div className="glass rounded-xl p-4">
            <h2 className="text-sm font-bold">Market report analytics</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Reads, card clicks, citation copies and JSON-feed opens on the weekly Palworld TCG market report.
            </p>
            <div className="mt-3">
              <ReportAnalytics />
            </div>
          </div>

          <PriceAutomationPanel />
          <AffiliatePanel />


          <div className="glass rounded-xl p-4">

            <h2 className="text-sm font-bold">Recent grants</h2>
            {log.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No packs granted yet.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {log.map((g) => {
                  const who = members.find((m) => m.id === g.granted_to)?.name ?? "Trainer";
                  return (
                    <li key={g.id} className="flex items-center justify-between gap-2">
                      <span className="truncate text-muted-foreground">
                        {who}
                        {g.note ? ` — ${g.note}` : ""}
                      </span>
                      <strong className="text-green-400">+{g.packs}</strong>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
